export function createCanvasTicker({
  elementToObserve,
  fps = 40,
  // If fps >= this value, skip timer throttling and use pure RAF
  unthrottledFPS = 55,
  threshold = 0.01,
  prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  onFrame, // (now, dt) => void
  onReduced, // () => void (optional)
} = {}) {
  let running = true;
  let last = performance.now();
  let rafId = null;
  let timerId = null;
  let reducedDrawn = false;

  const frameInterval = 1000 / fps;

  function clear() {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = null;
    if (timerId) clearTimeout(timerId);
    timerId = null;
  }

  function scheduleNext() {
    if (!running) return;

    if (fps >= unthrottledFPS) {
      rafId = requestAnimationFrame(tick);
      return;
    }

    timerId = setTimeout(() => {
      rafId = requestAnimationFrame(tick);
    }, frameInterval);
  }

  function tick(now) {
    // mark scheduled handles as consumed
    rafId = null;
    timerId = null;

    if (!running) return;

    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;

    if (prefersReducedMotion) {
      if (!reducedDrawn) {
        reducedDrawn = true;
        onFrame?.(now, 0);
        onReduced?.();
      }
      return; // don’t schedule more frames
    }

    onFrame?.(now, dt);
    scheduleNext();
  }

  function setRunning(next) {
    const was = running;
    running = !!next;

    // reset dt jump when resuming
    last = performance.now();

    if (!running) {
      clear();
    } else if (!was) {
      scheduleNext();
    }
  }

  // ✅ single removable visibility handler
  const onVis = () => setRunning(document.visibilityState === "visible");
  document.addEventListener("visibilitychange", onVis);

  const io = new IntersectionObserver(
    ([entry]) => {
      setRunning(entry.isIntersecting && document.visibilityState === "visible");
    },
    { threshold },
  );

  if (elementToObserve) io.observe(elementToObserve);

  return {
    start() {
      reducedDrawn = false;
      setRunning(true);
      if (!rafId && !timerId && running) scheduleNext(); // only if not already scheduled
    },
    stop() {
      setRunning(false);
    },
    destroy() {
      clear();
      document.removeEventListener("visibilitychange", onVis);
      io.disconnect();
    },
  };
}
